// src/components/classes/ClassesContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import classesService from '../../services/classesService';
import { useAuth } from '../auth/AuthContext';

const ClassesContext = createContext();

// Safe localStorage setter that handles quota exceeded errors
const safeSetLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn('ClassesProvider - localStorage quota exceeded, clearing old data');
      try {
        // Clear the specific key to free up space
        localStorage.removeItem(key);
        // Try one more time with cleared space
        localStorage.setItem(key, value);
        return true;
      } catch (retryError) {
        console.warn('ClassesProvider - Still unable to store to localStorage, disabling cache');
        return false;
      }
    }
    console.error('ClassesProvider - Error storing to localStorage:', error);
    return false;
  }
};

export const useClasses = () => {
  const context = useContext(ClassesContext);
  if (!context) {
    throw new Error('useClasses must be used within a ClassesProvider');
  }
  return context;
};

export const ClassesProvider = ({ children }) => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Load classes from localStorage as fallback
  const loadClassesFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('classtraks_classes');
      if (stored) {
        const parsedClasses = JSON.parse(stored);
        if (Array.isArray(parsedClasses) && parsedClasses.length > 0) {
          console.log('ClassesProvider - Loaded classes from localStorage:', parsedClasses);
          return parsedClasses;
        }
      }
    } catch (error) {
      console.error('Error loading classes from localStorage:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem('classtraks_classes');
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
    }
    return [];
  };

  // Default sample data for development
  const getDefaultClasses = () => [
    {
      id: 1,
      title: 'Beginning Sewing',
      instructor: 'Sarah Thompson',
      category: 'Garment Construction',
      skillLevel: 'Beginner',
      frequency: 'weekly',
      dayOfWeek: 'Monday',
      startMonth: '',
      endMonth: '',
      oneTimeDate: '',
      customDates: [],
      maxCapacity: 15,
      currentEnrollment: 8,
      startDate: '2025-07-15',
      endDate: '2025-08-15',
      startTime: '10:00',
      duration: 120,
      location: 'Room A',
      price: 150,
      description: 'Learn the fundamentals of sewing including basic stitches, threading a machine, and creating simple seams.',
      tags: ['Hand Sewing', 'Machine Basics', 'Threading', 'Seams'],
      specialInstructions: 'Bring your fabric scissors and basic sewing kit. Sewing machines provided.',
      imageUrl: null,
      status: 'active',
      availableSpots: 7,
      dateCreated: '2025-06-15T10:00:00Z',
      lastModified: '2025-06-20T14:30:00Z',
      projectId: 1,
      projectTitle: 'Tote Bag',
      projectDescription: 'Create a simple tote bag with handles',
      projectDifficulty: 'Beginner',
      projectDuration: '4 weeks',
      projectCategory: 'Sewing',
      projectMaterials: ['Cotton fabric', 'Thread', 'Scissors'],
      projectInstructions: 'Follow the pattern and sewing steps provided',
      projectImageUrl: null
    },
    {
      id: 2,
      title: 'Quilting Basics',
      instructor: 'Emily Chen',
      category: 'Quilting',
      skillLevel: 'Beginner',
      frequency: 'weekly',
      dayOfWeek: 'Wednesday',
      startMonth: '',
      endMonth: '',
      oneTimeDate: '',
      customDates: [],
      maxCapacity: 12,
      currentEnrollment: 10,
      startDate: '2025-07-17',
      endDate: '2025-08-17',
      startTime: '14:00',
      duration: 90,
      location: 'Design Lab',
      price: 120,
      description: 'Master the basics of quilting including fabric selection, cutting, piecing, and binding techniques.',
      tags: ['Piecing', 'Cutting', 'Binding', 'Quilting Patterns'],
      specialInstructions: 'Bring your rotary cutter and quilting ruler. Fabric provided for first project.',
      imageUrl: null,
      status: 'active',
      availableSpots: 2,
      dateCreated: '2025-06-10T09:00:00Z',
      lastModified: '2025-06-18T16:45:00Z',
      projectId: 2,
      projectTitle: 'Baby Quilt',
      projectDescription: 'Create a small baby quilt with simple squares',
      projectDifficulty: 'Intermediate',
      projectDuration: '3 weeks',
      projectCategory: 'Quilting',
      projectMaterials: ['Cotton fabric squares', 'Batting', 'Backing fabric'],
      projectInstructions: 'Piece together squares and quilt using simple straight lines',
      projectImageUrl: null
    },
    {
      id: 3,
      title: 'Pattern Making',
      instructor: 'Maria Rodriguez',
      category: 'Pattern Design',
      skillLevel: 'Intermediate',
      frequency: 'weekly',
      dayOfWeek: 'Friday',
      startMonth: '',
      endMonth: '',
      oneTimeDate: '',
      customDates: [],
      maxCapacity: 10,
      currentEnrollment: 6,
      startDate: '2025-07-19',
      endDate: '2025-08-23',
      startTime: '16:00',
      duration: 150,
      location: 'Lab B',
      price: 200,
      description: 'Learn to create custom patterns from measurements and alter existing patterns for perfect fit.',
      tags: ['Drafting', 'Measurements', 'Alterations', 'Fitting'],
      specialInstructions: 'Bring measuring tape and pattern paper. Basic sewing knowledge helpful.',
      imageUrl: null,
      status: 'active',
      availableSpots: 4,
      dateCreated: '2025-06-05T11:30:00Z',
      lastModified: '2025-06-22T10:15:00Z',
      projectId: null,
      projectTitle: null,
      projectDescription: null,
      projectDifficulty: null,
      projectDuration: null,
      projectCategory: null,
      projectMaterials: null,
      projectInstructions: null,
      projectImageUrl: null
    }
  ];

  // Fetch classes from database with localStorage fallback
  const fetchClasses = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Fetching classes from database...');
      const response = await classesService.getClasses(params);
      console.log('ClassesProvider - Database response:', response);
      
      // If we got classes from database, use them
      if (response.classes && response.classes.length > 0) {
        console.log('ClassesProvider - Using database classes');
        const transformedClasses = classesService.transformClassesFromBackend(response.classes);
        setClasses(transformedClasses);
        
              // Sync to localStorage for future fallback with error handling
        try {
          // Only store essential data to avoid quota issues
          const essentialData = transformedClasses.map(cls => ({
            id: cls.id,
            title: cls.title,
            instructor: cls.instructor,
            category: cls.category,
            skillLevel: cls.skillLevel,
            frequency: cls.frequency,
            maxCapacity: cls.maxCapacity,
            currentEnrollment: cls.currentEnrollment,
            startDate: cls.startDate,
            endDate: cls.endDate,
            startTime: cls.startTime,
            duration: cls.duration,
            location: cls.location,
            price: cls.price,
            status: cls.status,
            projectId: cls.projectId,
            projectTitle: cls.projectTitle,
            waitlistEnabled: cls.waitlistEnabled
          }));
          safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
        } catch (storageError) {
          console.warn('ClassesProvider - Error during class transformation:', storageError);
        }
        
        if (response.pagination) {
          setPagination(response.pagination);
        }
        
        return transformedClasses;
      }
      
      // If no database classes, check localStorage as fallback
      console.log('ClassesProvider - No database classes, checking localStorage...');
      const localClasses = loadClassesFromLocalStorage();
      if (localClasses.length > 0) {
        console.log('ClassesProvider - Using localStorage classes as fallback');
        setClasses(localClasses);
        return localClasses;
      }
      
      // If no localStorage data, start with empty array (no sample data)
      console.log('ClassesProvider - No classes found, starting with empty array');
      setClasses([]);
      return [];
      
    } catch (error) {
      console.error('ClassesProvider - Error fetching from database:', error);
      setError(error.message);
      
      // Fallback to localStorage on database error
      console.log('ClassesProvider - Database error, falling back to localStorage...');
      const localClasses = loadClassesFromLocalStorage();
      if (localClasses.length > 0) {
        setClasses(localClasses);
        return localClasses;
      }
      
      // Final fallback to empty array (no sample data)
      console.log('ClassesProvider - Using empty array as final fallback');
      setClasses([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get single class with localStorage fallback
  const getClass = async (id) => {
    try {
      console.log('ClassesProvider - Getting class with ID:', id);
      
      // Try database first
      try {
        const response = await classesService.getClass(id);
        console.log('ClassesProvider - Found class in database:', response);
        return response;
      } catch (dbError) {
        console.error('ClassesProvider - Database fetch failed, checking local classes:', dbError);
        
        // Fallback to local classes
        const localClass = classes.find(cls => 
          cls.id === id || cls.id === parseInt(id) || String(cls.id) === String(id)
        );
        
        if (localClass) {
          console.log('ClassesProvider - Found class in local state:', localClass);
          return localClass;
        }
        
        throw new Error('Class not found');
      }
    } catch (error) {
      console.error('ClassesProvider - Error fetching class:', error);
      setError(error.message);
      throw error;
    }
  };

  // Add new class to database with localStorage fallback
  const addClass = async (classData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Adding class to database:', classData);
      
      // Try to save to database
      try {
        const newClass = await classesService.createClass(classData);
        console.log('ClassesProvider - Class created in database:', newClass);
        
        // Add to local state
        setClasses(prev => {
          const updatedClasses = [newClass, ...prev];
          // Update localStorage with the new state
          safeSetLocalStorage('classtraks_classes', JSON.stringify(updatedClasses));
          return updatedClasses;
        });
        
        // Update pagination
        setPagination(prev => ({
          ...prev,
          totalItems: prev.totalItems + 1
        }));
        
        return newClass;
      } catch (dbError) {
        console.error('ClassesProvider - Database save failed, using localStorage:', dbError);
        
        // Fallback to localStorage if database fails
        // Generate a temporary UUID-like ID for localStorage
        const localClass = {
          ...classData,
          id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          dateCreated: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          currentEnrollment: 0,
          availableSpots: classData.maxCapacity || 0,
          status: 'active'
        };
        
        // Add to state
        setClasses(prev => {
          const updatedClasses = [localClass, ...prev];
          // Save to localStorage with the new state
          try {
            const essentialData = updatedClasses.map(cls => ({
              id: cls.id,
              title: cls.title,
              instructor: cls.instructor,
              category: cls.category,
              skillLevel: cls.skillLevel,
              status: cls.status,
              waitlistEnabled: cls.waitlistEnabled
            }));
            safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
          } catch (storageError) {
            console.warn('ClassesProvider - Cannot update localStorage:', storageError);
          }
          return updatedClasses;
        });
        
        return localClass;
      }
    } catch (error) {
      console.error('ClassesProvider - Error creating class:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update existing class with localStorage fallback
  const updateClass = async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Updating class:', id, updates);
      
      // Try database first
      try {
        const updatedClass = await classesService.updateClass(id, updates);
        console.log('ClassesProvider - Class updated in database:', updatedClass);
        
        // Update local state
        setClasses(prev => {
          const updatedClasses = prev.map(cls => 
            cls.id === id ? updatedClass : cls
          );
          // Update localStorage with the new state
          try {
            const essentialData = updatedClasses.map(cls => ({
              id: cls.id,
              title: cls.title,
              instructor: cls.instructor,
              category: cls.category,
              skillLevel: cls.skillLevel,
              status: cls.status,
              waitlistEnabled: cls.waitlistEnabled
            }));
            safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
          } catch (storageError) {
            console.warn('ClassesProvider - Cannot update localStorage:', storageError);
          }
          return updatedClasses;
        });
        
        return updatedClass;
      } catch (dbError) {
        console.error('ClassesProvider - Database update failed, using localStorage:', dbError);
        
        // Fallback to localStorage
        const existingClass = classes.find(c => c.id === id || c.id === parseInt(id));
        if (!existingClass) {
          throw new Error('Class not found in local state');
        }
        const updatedClass = {
          ...existingClass,
          ...updates,
          id: id, // Keep the original ID
          lastModified: new Date().toISOString()
        };
        
        // Update state
        setClasses(prev => {
          const updatedClasses = prev.map(cls => 
            (cls.id === id || cls.id === parseInt(id)) ? updatedClass : cls
          );
          // Save to localStorage with the new state
          try {
            const essentialData = updatedClasses.map(cls => ({
              id: cls.id,
              title: cls.title,
              instructor: cls.instructor,
              category: cls.category,
              skillLevel: cls.skillLevel,
              status: cls.status,
              waitlistEnabled: cls.waitlistEnabled
            }));
            safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
          } catch (storageError) {
            console.warn('ClassesProvider - Cannot update localStorage:', storageError);
          }
          return updatedClasses;
        });
        
        return updatedClass;
      }
    } catch (error) {
      console.error('ClassesProvider - Error updating class:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete single class with localStorage fallback
  const deleteClass = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Deleting class:', id);
      
      // Try database first
      try {
        await classesService.deleteClass(id);
        console.log('ClassesProvider - Class deleted from database');
      } catch (dbError) {
        console.error('ClassesProvider - Database delete failed:', dbError);
        // Continue with local deletion even if database fails
      }
      
      // Remove from local state (works for both DB and localStorage classes)
      setClasses(prev => {
        const updatedClasses = prev.filter(cls => 
          cls.id !== id && cls.id !== parseInt(id)
        );
        // Update localStorage with the new state
        try {
          const essentialData = updatedClasses.map(cls => ({
            id: cls.id,
            title: cls.title,
            instructor: cls.instructor,
            category: cls.category,
            skillLevel: cls.skillLevel,
            status: cls.status
          }));
          safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
        } catch (storageError) {
          console.warn('ClassesProvider - Cannot update localStorage:', storageError);
        }
        return updatedClasses;
      });
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        totalItems: prev.totalItems - 1
      }));
      
      return true;
    } catch (error) {
      console.error('ClassesProvider - Error deleting class:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete multiple classes with localStorage fallback
  const deleteClasses = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Deleting multiple classes:', ids);
      
      // Try database deletions
      for (const id of ids) {
        try {
          await classesService.deleteClass(id);
        } catch (dbError) {
          console.error(`ClassesProvider - Failed to delete class ${id} from database:`, dbError);
          // Continue with other deletions
        }
      }
      
      // Remove from local state
      setClasses(prev => {
        const updatedClasses = prev.filter(cls => 
          !ids.includes(cls.id) && !ids.includes(parseInt(cls.id))
        );
        // Update localStorage with the new state
        try {
          const essentialData = updatedClasses.map(cls => ({
            id: cls.id,
            title: cls.title,
            instructor: cls.instructor,
            category: cls.category,
            skillLevel: cls.skillLevel,
            status: cls.status
          }));
          safeSetLocalStorage('classtraks_classes', JSON.stringify(essentialData));
        } catch (storageError) {
          console.warn('ClassesProvider - Cannot update localStorage:', storageError);
        }
        return updatedClasses;
      });
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        totalItems: prev.totalItems - ids.length
      }));
      
      return true;
    } catch (error) {
      console.error('ClassesProvider - Error deleting classes:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone single class with localStorage fallback
  const cloneClass = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ClassesProvider - Cloning class:', id);
      
      // Find the class to clone
      const originalClass = classes.find(cls => 
        cls.id === id || cls.id === parseInt(id)
      );
      
      if (!originalClass) {
        throw new Error('Class not found');
      }

      // Only include the fields that the backend API expects
      const clonedData = {
        title: `${originalClass.title} (Copy)`,
        instructor: originalClass.instructor,
        category: originalClass.category,
        skillLevel: originalClass.skillLevel,
        frequency: originalClass.frequency || 'Weekly',
        dayOfWeek: originalClass.dayOfWeek || null,
        startMonth: originalClass.startMonth || null,
        endMonth: originalClass.endMonth || null,
        oneTimeDate: originalClass.oneTimeDate || null,
        customDates: originalClass.customDates || null,
        maxCapacity: originalClass.maxCapacity,
        startDate: originalClass.startDate || null,
        endDate: originalClass.endDate || null,
        startTime: originalClass.startTime || '10:00',
        duration: originalClass.duration || 60,
        location: originalClass.location,
        price: originalClass.price,
        description: originalClass.description,
        tags: originalClass.tags || [],
        specialInstructions: originalClass.specialInstructions || '',
        minimumAge: originalClass.minimumAge || null,
        prerequisiteClassIds: originalClass.prerequisiteClassIds || [],
        projectId: originalClass.projectId || null
      };

      return await addClass(clonedData);
    } catch (error) {
      console.error('ClassesProvider - Error cloning class:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone multiple classes
  const cloneClasses = async (ids) => {
    try {
      const clonedClasses = [];
      
      for (const id of ids) {
        const clonedClass = await cloneClass(id);
        clonedClasses.push(clonedClass);
      }
      
      return clonedClasses;
    } catch (error) {
      console.error('Error cloning classes:', error);
      setError(error.message);
      throw error;
    }
  };

  // Get class statistics
  const getClassStats = async () => {
    try {
      const stats = await classesService.getClassStats();
      return stats;
    } catch (error) {
      console.error('Error fetching class stats:', error);
      setError(error.message);
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Refresh classes from database
  const refreshClasses = () => {
    return fetchClasses();
  };

  // Clear localStorage cache and refresh from database
  const clearCache = () => {
    try {
      localStorage.removeItem('classtraks_classes');
      console.log('ClassesProvider - Cleared localStorage cache');
    } catch (error) {
      console.error('ClassesProvider - Error clearing localStorage:', error);
    }
    return fetchClasses();
  };

  // Load initial data and refetch when user changes
  useEffect(() => {
    if (user) {
      // Clear existing classes and refetch for new user
      setClasses([]);
      fetchClasses();
    }
  }, [user?.id]); // Refetch when user ID changes

  const value = {
    classes,
    loading,
    error,
    pagination,
    fetchClasses,
    getClass,
    addClass,
    updateClass,
    deleteClass,
    deleteClasses,
    cloneClass,
    cloneClasses,
    getClassStats,
    clearError,
    refreshClasses,
    clearCache
  };

  return (
    <ClassesContext.Provider value={value}>
      {children}
    </ClassesContext.Provider>
  );
};